import { useEffect, useRef, useState } from "react";
import { TrashSimple } from "@phosphor-icons/react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@breakingthelines/design-system";

import { LoadingSpinner } from "@/components/common/loading-spinner";
import {
  deleteEntityAsset,
  getEntityDetail,
  setEntityOverride,
  uploadEntityAsset,
  type EntityDetail,
  type EntityTextSource,
} from "@/lib/admin-api";
import {
  ENTITY_KINDS,
  acceptAttribute,
  acceptedFormatsLabel,
  bespokeRoleTiles,
  entityAssetRoleSpec,
  entityKindForId,
  entityKindSpec,
  formatBytes,
  formatDateTime,
  identityImageLayer,
  isAcceptedFormat,
  isCanonicalEntityId,
  maxBytesFor,
  previewUrl,
  providerImageLayers,
  type EntityAssetRole,
  type EntityImageLayer,
  type EntityKind,
} from "@/lib/entity-assets";

import { EntityPicker } from "./entity-picker";

/**
 * The Entity subtab of Entity assets: the entity picker plus the loaded
 * entity's card - its imagery and its name and biography. Split out of the
 * single Entity assets page (#72-#84) so it and the Corrections list are two
 * tabs instead of one long stack - see entity-assets-layout.tsx and
 * router.tsx.
 *
 * The tab was called "Editor" until the owner pointed out that BTL already has
 * an Editor - @breakingthelines/editor, the rich text editor in studio and the
 * pages CMS - and there is no such thing on this screen.
 *
 * TWO PLANES, ONE TAB. Imagery is objects under media/btl/** owned by
 * content-service; text is game-service's override row applied by
 * identity-server at read time. They share a screen because an operator fixing
 * how an entity presents wants both, and nothing else. The proto messages have
 * no fields in common and this tab keeps them apart.
 *
 * # THE COPY NAMES WHAT THINGS ARE, NOT WHERE THEY LIVE
 *
 * The owner's call on the first cut of this page: "Club crest - Name from
 * baked identity facts... what does this mean?" Layer names, storage prefixes
 * and source citations are the pipeline documenting itself. Every label here
 * answers the operator's question instead - what is this, has somebody changed
 * it, what happens if I upload - and the source taxonomy survives only as the
 * one word "edited".
 *
 * # ARTWORK IS EDITED PER ROLE
 *
 * A bespoke asset is (entityId, role), not (entityId, kind). A player has an
 * avatar AND a hero, at two keys, and each has its own tile with its own upload
 * and its own delete: replacing the portrait must not touch the headshot. Under
 * the kind model they shared one slot and one control, so uploading either
 * overwrote the other, and an avatar could not be written at all.
 *
 * Only BTL's own tiles are editable. The provider and registry tiles are
 * mirrors of somebody else's asset and this page has no write path to them.
 *
 * NO PER-ENTITY COVERAGE API. Every image address is built from the canonical
 * id and rendered optimistically; the browser's own 200/404 is the source of
 * truth for whether the file exists (ADR-036). A "Not found" tile is an answer,
 * not a failure. The artwork rows on Corrections are a different question:
 * they enumerate the bucket prefix and cannot say whether any particular
 * entity has art.
 *
 * # THE `id` QUERY PARAM IS THE CROSS-TAB LOAD PATH
 *
 * A Corrections row's "Load" action, and any future one like it, cannot
 * reach into this component's state directly - it is a different route,
 * possibly a different mount entirely. It navigates here instead, to
 * `/entities/assets/entity?id=<entityId>`, and the effect below is the other
 * end of that contract: it reads `id` once on arrival and loads it. This is
 * also what makes a reload of that exact URL load the same entity rather than
 * coming back empty - the id lives in the address bar, not in memory that dies
 * with the tab.
 *
 * Picking an entity by any other route on THIS tab (search, the id field, the
 * kind selector) does not touch the URL, so it cannot re-trigger the effect
 * against itself.
 */

type Status =
  | { kind: "idle" }
  | { kind: "busy"; message: string }
  | { kind: "done"; message: string };

interface OverrideForm {
  displayName: string;
  biography: string;
}

const EMPTY_FORM: OverrideForm = { displayName: "", biography: "" };

/**
 * The one word the page says about where a value came from.
 *
 * The operator's question is "has somebody changed this", not which layer of
 * the pipeline answered. Every other source - the registry's own facts, the
 * enrichment pass - is simply the entity as it stands, and naming those in the
 * UI documented the pipeline instead of the entity (the owner's call on #85's
 * "Name from baked identity facts"). The save and clear semantics below are
 * unchanged; only the vocabulary on screen is.
 */
function editedMark(source: EntityTextSource | undefined) {
  return source === "override" ? (
    <span className="text-muted-foreground text-[11px] leading-none">edited</span>
  ) : null;
}

/** One probe tile. Read-only on its own; the edit controls sit beside the set. */
function LayerTile({
  layer,
  entityId,
  missing,
  cacheBustAt,
  onMissing,
}: {
  layer: EntityImageLayer;
  entityId: string;
  missing: boolean;
  cacheBustAt: number | null;
  onMissing: (layerId: string, missing: boolean) => void;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div
        className={`relative flex aspect-square items-center justify-center overflow-hidden rounded border bg-black ${
          layer.bespoke ? "border-grey-300" : "border-border"
        }`}
      >
        {missing ? (
          <span className="text-muted-foreground text-[11px]">None</span>
        ) : (
          <img
            className="max-h-[80%] max-w-[80%] object-contain"
            src={layer.bespoke ? previewUrl(layer.url, cacheBustAt) : layer.url}
            alt={`${layer.label} for ${entityId}`}
            onError={() => onMissing(layer.id, true)}
            onLoad={() => onMissing(layer.id, false)}
          />
        )}
      </div>
      <span className="text-xs font-semibold text-white">{layer.label}</span>
      {/* The layer name and the CDN address used to sit here. Both were the
          storage model on screen: "provider layer", "apifootball/player/..." -
          nothing an operator uploading a crest does anything with. What
          replaces them is the section the tile sits in. */}
      {layer.credit ? (
        <span className="text-muted-foreground text-[10px] leading-snug break-words">
          {layer.credit}
        </span>
      ) : null}
    </div>
  );
}

export function EntityEditorPage() {
  const [searchParams] = useSearchParams();

  const [kind, setKind] = useState<EntityKind>("crest");
  const [entityIdInput, setEntityIdInput] = useState("");

  // The entity currently loaded, which is NOT the input: the previews and the
  // form must never drift onto an id the operator is halfway through typing.
  const [loaded, setLoaded] = useState<{ kind: EntityKind; entityId: string } | null>(null);
  const [detail, setDetail] = useState<EntityDetail | null>(null);

  const [form, setForm] = useState<OverrideForm>(EMPTY_FORM);
  // What the form was preloaded with - the values in force when the entity
  // last loaded (or last saved). Save is a no-op against these: it is how the
  // page tells "the operator changed something" from "the operator reopened
  // the same text", and it is what a per-field diff is measured against below.
  const [initialForm, setInitialForm] = useState<OverrideForm>(EMPTY_FORM);
  const [loadingEntity, setLoadingEntity] = useState(false);

  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [error, setError] = useState<string | null>(null);

  // Bumped after this page writes art, to cache-bust its own previews. Stored
  // assets carry a 5-minute cache on an unversioned key, so without this an
  // operator replacing bad art could see the old file for a few minutes and
  // think the upload failed.
  const [writtenAt, setWrittenAt] = useState<number | null>(null);
  const [missingLayers, setMissingLayers] = useState<Set<string>>(new Set());

  // One file input per bespoke role. A player has two and they are independent,
  // so a single ref would put the wrong file behind the wrong Upload button.
  const fileInputs = useRef<Partial<Record<EntityAssetRole, HTMLInputElement | null>>>({});
  const [files, setFiles] = useState<Partial<Record<EntityAssetRole, File | null>>>({});

  const spec = entityKindSpec(kind);
  const trimmedId = entityIdInput.trim();
  const idLooksCanonical = trimmedId.length > 0 && isCanonicalEntityId(kind, trimmedId);

  const existingOverride = detail?.override ?? null;

  // Per-field "has the operator touched this" - measured against the PRELOAD,
  // not against blank. An unopened field is never "changed" just because it
  // shows text; Save stays disabled until one of these is true, which is what
  // makes a no-edit Save impossible rather than merely harmless.
  const nameChanged = form.displayName.trim() !== initialForm.displayName.trim();
  const bioChanged = form.biography.trim() !== initialForm.biography.trim();
  const overrideDirty = nameChanged || bioChanged;

  // Bespoke art, grouped by role, because a role is what an upload and a delete
  // are scoped to.
  const roleTiles = loaded ? bespokeRoleTiles(loaded.kind, loaded.entityId) : [];

  // Everything this page cannot write: the mirrored provider addresses, plus
  // identity's own image when it has one that is safe to render.
  const referenceLayers = (() => {
    if (!loaded) return [];
    const provider = providerImageLayers(loaded.kind, loaded.entityId);
    const identity = identityImageLayer(detail?.identity?.imageUrl, detail?.identity?.imageCredit);
    return identity ? [...provider, identity] : provider;
  })();

  function resetEntityState() {
    setStatus({ kind: "idle" });
    setError(null);
    setWrittenAt(null);
    setMissingLayers(new Set());
    setFiles({});
    for (const input of Object.values(fileInputs.current)) {
      if (input) input.value = "";
    }
  }

  // Preloads with the values CURRENTLY IN FORCE - detail.displayName and
  // detail.biography are already resolved (override if one is set, else the
  // baked or enriched text the card above shows), so the operator edits in
  // place instead of typing into fields that look blank until they touch
  // them. See handleSaveOverride for the other half: saving without
  // changing anything must not write a new override equal to this text.
  function formFromDetail(next: EntityDetail): OverrideForm {
    return { displayName: next.displayName, biography: next.biography };
  }

  function setFormFromDetail(next: EntityDetail) {
    const loadedForm = formFromDetail(next);
    setForm(loadedForm);
    setInitialForm(loadedForm);
  }

  async function loadEntity(nextKind: EntityKind, entityId: string) {
    resetEntityState();
    setLoadingEntity(true);
    setLoaded({ kind: nextKind, entityId });
    setDetail(null);
    try {
      const response = await getEntityDetail(entityId);
      setDetail(response);
      setFormFromDetail(response);
      // identity is authoritative about what an entity IS, so a loaded kind
      // that disagrees with the selector follows identity rather than the
      // dropdown someone left on the wrong value.
      if (response.type && response.type !== nextKind) {
        setKind(response.type);
        setLoaded({ kind: response.type, entityId });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this entity.");
      setDetail(null);
      setForm(EMPTY_FORM);
      setInitialForm(EMPTY_FORM);
    } finally {
      setLoadingEntity(false);
    }
  }

  // The other end of a cross-tab Load: a Corrections row navigates to this
  // route with ?id=<entityId>, and this is what picks it up - once,
  // on arrival, from whatever id the URL carries. A reload of that same URL
  // runs this exact effect again, which is what makes the load survive a
  // refresh rather than depending on state that only ever lived in memory.
  //
  // Loading an entity from inside this tab (search, the id field, the kind
  // selector) does not write to the URL, so none of that re-fires this effect
  // against its own load.
  useEffect(() => {
    const idParam = searchParams.get("id");
    if (!idParam) return;
    const kindForId = entityKindForId(idParam);
    if (!kindForId) return;
    setEntityIdInput(idParam);
    setKind(kindForId);
    void loadEntity(kindForId, idParam);
    // Only `id` drives this. See the comment above for why re-running it on
    // every local state change would be wrong.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  function markLayer(layerId: string, missing: boolean) {
    setMissingLayers((prev) => {
      if (prev.has(layerId) === missing) return prev;
      const next = new Set(prev);
      if (missing) next.add(layerId);
      else next.delete(layerId);
      return next;
    });
  }

  async function handleUpload(role: EntityAssetRole) {
    const file = files[role] ?? null;
    if (!loaded || !file) return;
    setError(null);

    const roleSpec = entityAssetRoleSpec(role);
    // Checked against the ROLE, not the kind. A player takes WebP for both of
    // its roles, but a crest takes SVG and nothing photographic does.
    if (!isAcceptedFormat(role, file.type)) {
      setError(`The ${roleSpec.label.toLowerCase()} must be ${acceptedFormatsLabel(role)}.`);
      return;
    }
    const limit = maxBytesFor(file.type);
    if (file.size > limit) {
      setError(`File is ${formatBytes(file.size)}. The limit is ${formatBytes(limit)}.`);
      return;
    }

    setStatus({ kind: "busy", message: "Uploading" });

    try {
      // ONE PATH FOR EVERY FORMAT. This used to fork: SVG through the service
      // so it could be sanitised, raster presigned and PUT straight to R2. The
      // fork is gone because a presigned PUT never passes through
      // content-service, and both formats now need something done to them
      // there. An SVG is sanitised. A PNG, a JPEG or a WebP is decoded, sized
      // to the imagery mandate's geometry and re-encoded as WebP. A presigned
      // raster would be stored exactly as picked, at whatever shape it
      // happened to be, and no later pass could fix it because the upload
      // replaced the only copy.
      //
      // The stored size and key come back and are deliberately not shown: the
      // operator uploaded a file and the tile above now renders it, at the
      // object key and public URL the response named.
      await uploadEntityAsset(role, loaded.entityId, file);
      setStatus({ kind: "done", message: `${roleSpec.label} uploaded.` });

      setWrittenAt(Date.now());
      setMissingLayers(new Set());
      setFiles((prev) => ({ ...prev, [role]: null }));
      const input = fileInputs.current[role];
      if (input) input.value = "";
    } catch (err) {
      setStatus({ kind: "idle" });
      setError(err instanceof Error ? err.message : "Upload failed.");
    }
  }

  async function handleDeleteRole(role: EntityAssetRole) {
    if (!loaded) return;
    const roleSpec = entityAssetRoleSpec(role);
    const otherRoles = entityKindSpec(loaded.kind).roles.filter((value) => value !== role);
    const scope =
      otherRoles.length > 0
        ? ` The ${otherRoles.map((value) => entityAssetRoleSpec(value).label.toLowerCase()).join(" and ")} stays.`
        : "";
    // Named, not id'd: the operator picked this entity by name and the confirm
    // is the last place to notice it is the wrong one.
    const subject = detail?.displayName || loaded.entityId;
    const confirmed = window.confirm(
      `Remove the ${roleSpec.label.toLowerCase()} for ${subject}? The provider image shows instead, or a monogram where there is none.${scope}`,
    );
    if (!confirmed) return;

    setError(null);
    setStatus({ kind: "busy", message: "Removing" });
    try {
      const response = await deleteEntityAsset(role, loaded.entityId);
      setStatus({
        kind: "done",
        message:
          response.deletedKeys.length > 0
            ? `${roleSpec.label} removed.`
            : `There was no ${roleSpec.label.toLowerCase()} to remove.`,
      });
      setWrittenAt(Date.now());
      setMissingLayers(new Set());
    } catch (err) {
      setStatus({ kind: "idle" });
      setError(err instanceof Error ? err.message : "Could not remove the artwork.");
    }
  }

  /**
   * Builds the FULL-ROW payload for a save that is not an explicit Clear.
   *
   * The form preloads with the EFFECTIVE text (see formFromDetail), so
   * sending it back unedited would write an override equal to the baked
   * facts - freezing the name and biography against every future baked
   * update, which is the opposite of "nothing changed". Per field:
   *
   *   - untouched: resend whatever the stored override already held for that
   *     field (often empty - there was no override on it), so a field the
   *     operator never opened is never the reason a row gets written or
   *     rewritten.
   *   - edited down to exactly the original text: send empty, dropping the
   *     override for that field. This is the revert - it must NOT freeze the
   *     original text as an override, the same failure mode as the preload
   *     case, reachable by typing instead of by silence.
   *   - edited to anything else: send the new text.
   *
   * Comparing "edited back to" against the ORIGINAL (identity's baked text),
   * not against the enrichment-filled text the operator may have started
   * from, is deliberate: a field that resolves through Wikidata has no
   * override row at either end of this decision, and the untouched branch
   * above already leaves it alone.
   */
  function buildOverridePayload(): { displayName: string; biography: string } {
    const originalName = detail?.identity?.name?.trim() ?? "";
    const originalBiography = detail?.identity?.biography?.trim() ?? "";
    const storedOverrideName = existingOverride?.displayName.trim() ?? "";
    const storedOverrideBiography = existingOverride?.biography.trim() ?? "";

    const nextName = form.displayName.trim();
    const nextBiography = form.biography.trim();

    return {
      displayName: nameChanged ? (nextName === originalName ? "" : nextName) : storedOverrideName,
      biography: bioChanged
        ? nextBiography === originalBiography
          ? ""
          : nextBiography
        : storedOverrideBiography,
    };
  }

  async function handleSaveOverride(clearing: boolean) {
    if (!loaded) return;
    if (!clearing && !overrideDirty) return; // the Save button is disabled for this; defensive only
    if (clearing) {
      const confirmed = window.confirm(
        `Clear the correction for ${detail?.displayName || loaded.entityId}? The name and biography go back to the originals.`,
      );
      if (!confirmed) return;
    }

    const payload = clearing ? { displayName: "", biography: "" } : buildOverridePayload();

    setError(null);
    setStatus({ kind: "busy", message: clearing ? "Clearing" : "Saving" });
    try {
      const response = await setEntityOverride(loaded.entityId, payload);
      setStatus({
        kind: "done",
        message: response.cleared ? "Correction cleared." : "Correction saved.",
      });

      // Re-read rather than patching the detail in place. The name and
      // biography in force, and whether a correction is the thing in force,
      // are resolved server-side against identity's own precedence;
      // recomputing them here would be a second implementation of that
      // precedence, drifting.
      try {
        const refreshed = await getEntityDetail(loaded.entityId);
        setDetail(refreshed);
        setFormFromDetail(refreshed);
      } catch {
        // The write landed. A failed re-read leaves the panel showing the
        // pre-write state, which is stale but true of nothing worse than this
        // browser; saying the save failed would be a lie.
      }
    } catch (err) {
      setStatus({ kind: "idle" });
      setError(err instanceof Error ? err.message : "Could not save the correction.");
    }
  }

  const busy = status.kind === "busy";
  const overrideBlocked = detail?.overrideUnavailable ?? false;

  return (
    <div className="flex w-full min-w-0 flex-col gap-6 pb-16">
      <div className="min-w-0">
        <h1 className="m-0 text-2xl font-semibold tracking-tight text-white md:text-3xl">
          Entity assets
        </h1>
        <p className="text-muted-foreground m-0 mt-2 max-w-[620px] text-sm leading-relaxed">
          Artwork and name corrections for one football entity. Uploaded artwork is not on public
          pages yet.
        </p>
      </div>

      {/* --- picker ------------------------------------------------------ */}
      <section className="glass-card rounded-btl-md flex min-w-0 flex-col gap-5 p-5">
        <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-[minmax(160px,1fr)_minmax(240px,2fr)]">
          <label className="flex min-w-0 flex-col gap-2">
            <span className="text-xs font-semibold text-white">Kind</span>
            <Select
              value={kind}
              onValueChange={(value: string) => {
                setKind(value as EntityKind);
                setLoaded(null);
                setDetail(null);
                setForm(EMPTY_FORM);
                setInitialForm(EMPTY_FORM);
                resetEntityState();
              }}
            >
              {/* Named explicitly: the wrapping <label> does not name a
                  button, and this page has several comboboxes. */}
              <SelectTrigger aria-label="Kind">
                <SelectValue>
                  {(value: string | null) =>
                    value ? entityKindSpec(value as EntityKind).label : "Select a kind"
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {ENTITY_KINDS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <EntityPicker
            onSelect={(entity) => {
              setEntityIdInput(entity.entityId);
              setKind(entity.type);
              void loadEntity(entity.type, entity.entityId);
            }}
          />
        </div>

        {/* The id lane stays. It reaches the two things search cannot: an
            entity renamed by an override, which search still indexes under its
            baked name, and a stadium, which the identity artifact does not
            hold at all. */}
        <div className="grid min-w-0 grid-cols-1 items-end gap-4 md:grid-cols-[minmax(0,1fr)_auto]">
          <label className="flex min-w-0 flex-col gap-2">
            <span className="text-xs font-semibold text-white">Entity ID</span>
            <Input
              placeholder={`${spec.idPrefix}42`}
              value={entityIdInput}
              aria-label="Entity ID"
              onChange={(event) => setEntityIdInput(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && idLooksCanonical) {
                  void loadEntity(kind, trimmedId);
                }
              }}
            />
          </label>

          <Button
            className="w-full md:w-auto"
            disabled={!idLooksCanonical || loadingEntity}
            onClick={() => void loadEntity(kind, trimmedId)}
          >
            Load
          </Button>
        </div>

        {/* One clause, and it names what is true of the input rather than
            teaching the id scheme. The helper that spelled out
            "Ids for this kind start btl_football_team_ and end in letters or
            digits" is gone: the prefix is storage vocabulary, and search is
            how an operator reaches an entity now. */}
        {trimmedId.length > 0 && !idLooksCanonical ? (
          <p className="text-destructive m-0 text-xs leading-relaxed">Not an id for this kind.</p>
        ) : null}
      </section>

      {loadingEntity ? <LoadingSpinner /> : null}

      {loaded && !loadingEntity ? (
        <>
          {/* --- who this is ----------------------------------------------
              The name, the id it is filed under, and what kind of thing it
              is. Where each value came from is not on this card: an operator
              reads the name, and the one fact they act on is whether somebody
              has already changed it. */}
          <section className="glass-card rounded-btl-md flex min-w-0 flex-col gap-5 p-5">
            <div className="min-w-0">
              <h2 className="m-0 text-xl font-semibold tracking-tight break-words text-white">
                {detail?.displayName || loaded.entityId}
              </h2>
              <p className="text-muted-foreground m-0 mt-1 font-mono text-[11px] break-all">
                {loaded.entityId}
              </p>
              <p className="text-muted-foreground m-0 mt-2 flex flex-wrap items-center gap-2 text-xs">
                <span>{entityKindSpec(loaded.kind).label}</span>
                {detail?.displayNameSource === "override" ? <span>·</span> : null}
                {editedMark(detail?.displayNameSource)}
              </p>
            </div>

            {detail?.identityUnavailable ? (
              <p className="text-muted-foreground m-0 text-xs leading-relaxed">
                Names are unavailable right now, so this entity shows as its id. Artwork and
                corrections still save. {detail.identityError}
              </p>
            ) : null}

            {detail && !detail.identityUnavailable && !detail.identityFound ? (
              <p className="text-muted-foreground m-0 text-xs leading-relaxed">
                This entity has no name on file. Its artwork can still be replaced.
              </p>
            ) : null}

            <div className="border-border min-w-0 border-0 border-t pt-5">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="m-0 text-sm font-semibold text-white">Biography</h3>
                {editedMark(detail?.biographySource)}
              </div>
              {detail?.biography ? (
                <p className="m-0 mt-2 max-w-[70ch] text-sm leading-relaxed break-words text-white">
                  {detail.biography}
                </p>
              ) : (
                <p className="text-muted-foreground m-0 mt-2 text-sm">No biography.</p>
              )}
            </div>
          </section>

          {/* --- artwork, one editable block per role ---------------------- */}
          <section className="glass-card rounded-btl-md flex min-w-0 flex-col gap-5 p-5">
            <div className="min-w-0">
              <h2 className="m-0 text-[15px] font-semibold tracking-tight text-white">Artwork</h2>
              {/* Only said when there is more than one, because that is the
                  only case where it changes what an operator does. A single
                  role needs no sentence explaining that it is one file. */}
              {roleTiles.length > 1 ? (
                <p className="text-muted-foreground m-0 mt-1 text-xs leading-relaxed">
                  Each one is a separate image. Replacing one leaves the other alone.
                </p>
              ) : null}
            </div>

            {roleTiles.map((tile, index) => {
              const file = files[tile.role] ?? null;
              return (
                <div className="flex min-w-0 flex-col gap-4" key={tile.role}>
                  {index > 0 ? <hr className="border-border m-0 border-0 border-t" /> : null}

                  <div className="min-w-0">
                    <h3 className="m-0 text-sm font-semibold text-white">{tile.spec.label}</h3>
                    <p className="text-muted-foreground m-0 mt-1 text-xs leading-relaxed">
                      {tile.spec.blurb}
                    </p>
                  </div>

                  <div className="grid min-w-0 grid-cols-2 gap-4 sm:grid-cols-[repeat(auto-fill,minmax(150px,1fr))]">
                    {tile.layers.map((layer) => (
                      <LayerTile
                        key={layer.id}
                        layer={layer}
                        entityId={loaded.entityId}
                        missing={missingLayers.has(layer.id)}
                        cacheBustAt={writtenAt}
                        onMissing={markLayer}
                      />
                    ))}
                  </div>

                  <div className="flex min-w-0 flex-wrap items-center gap-3">
                    <input
                      ref={(element) => {
                        fileInputs.current[tile.role] = element;
                      }}
                      className="text-muted-foreground max-w-full text-xs"
                      type="file"
                      aria-label={`${tile.spec.label} file`}
                      accept={acceptAttribute(tile.role)}
                      onChange={(event) => {
                        const picked = event.currentTarget.files?.[0] ?? null;
                        setFiles((prev) => ({ ...prev, [tile.role]: picked }));
                      }}
                    />
                    <Button
                      className="w-full md:w-auto"
                      disabled={!file || busy}
                      onClick={() => void handleUpload(tile.role)}
                    >
                      Upload {tile.spec.label.toLowerCase()}
                    </Button>
                    <Button
                      className="w-full md:w-auto"
                      variant="outline"
                      disabled={busy}
                      onClick={() => void handleDeleteRole(tile.role)}
                    >
                      <TrashSimple size={14} weight="bold" /> Remove
                    </Button>
                  </div>
                  <p className="text-muted-foreground m-0 text-xs leading-relaxed">
                    {tile.spec.formatHint}
                  </p>
                </div>
              );
            })}
          </section>

          {/* --- what this page cannot write ------------------------------
              The one thing worth saying about these is what happens to them
              when the operator uploads: BTL's own artwork is what shows. */}
          <section className="glass-card rounded-btl-md flex min-w-0 flex-col gap-5 p-5">
            <div className="min-w-0">
              <h2 className="m-0 text-[15px] font-semibold tracking-tight text-white">
                Other images
              </h2>
              <p className="text-muted-foreground m-0 mt-1 text-xs leading-relaxed">
                Read only. The artwork above shows in place of these.
              </p>
            </div>

            {referenceLayers.length > 0 ? (
              <div className="grid min-w-0 grid-cols-2 gap-4 sm:grid-cols-[repeat(auto-fill,minmax(150px,1fr))]">
                {referenceLayers.map((layer) => (
                  <LayerTile
                    key={layer.id}
                    layer={layer}
                    entityId={loaded.entityId}
                    missing={missingLayers.has(layer.id)}
                    cacheBustAt={writtenAt}
                    onMissing={markLayer}
                  />
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground m-0 text-xs leading-relaxed">
                There is no provider image for stadiums. This one shows its artwork or a monogram.
              </p>
            )}
          </section>

          {/* --- the correction ------------------------------------------
              Preloaded with the text in force, so the operator edits in
              place. Save is disabled until something differs from what
              loaded - the fields already show the current name and
              biography, so an unopened Save press has nothing to do. */}
          <section className="glass-card rounded-btl-md flex min-w-0 flex-col gap-5 p-5">
            <div className="min-w-0">
              <h2 className="m-0 text-[15px] font-semibold tracking-tight text-white">
                Name and biography
              </h2>
              <p className="text-muted-foreground m-0 mt-1 text-xs leading-relaxed">
                These replace the name and biography wherever this entity appears. Setting a field
                back to its original text removes the correction on that field.
              </p>
            </div>

            {overrideBlocked ? (
              <p className="text-muted-foreground m-0 text-xs leading-relaxed">
                Corrections cannot be saved right now. {detail?.overrideError}
              </p>
            ) : null}

            <div className="grid min-w-0 grid-cols-1 gap-4">
              <label className="flex min-w-0 flex-col gap-2">
                <span className="text-xs font-semibold text-white">Name</span>
                <Input
                  value={form.displayName}
                  aria-label="Name"
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setForm((prev) => ({ ...prev, displayName: value }));
                  }}
                />
              </label>

              <label className="flex min-w-0 flex-col gap-2">
                <span className="text-xs font-semibold text-white">Biography</span>
                <Textarea
                  rows={5}
                  value={form.biography}
                  aria-label="Biography"
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setForm((prev) => ({ ...prev, biography: value }));
                  }}
                />
              </label>
            </div>

            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <Button
                className="w-full md:w-auto"
                disabled={busy || overrideBlocked || !overrideDirty}
                onClick={() => void handleSaveOverride(false)}
              >
                Save
              </Button>
              <Button
                className="w-full md:w-auto"
                variant="outline"
                disabled={busy || overrideBlocked || !existingOverride}
                onClick={() => void handleSaveOverride(true)}
              >
                Clear
              </Button>
              {existingOverride ? (
                <span className="text-muted-foreground text-xs leading-relaxed">
                  Edited by {existingOverride.updatedBy || "unknown"} on{" "}
                  {formatDateTime(existingOverride.updatedAt)}
                </span>
              ) : (
                <span className="text-muted-foreground text-xs leading-relaxed">
                  No correction yet.
                </span>
              )}
            </div>
          </section>

          {status.kind !== "idle" ? (
            <p className="text-muted-foreground m-0 text-xs leading-relaxed">{status.message}</p>
          ) : null}
          {error ? <p className="text-destructive m-0 text-xs leading-relaxed">{error}</p> : null}
        </>
      ) : null}
    </div>
  );
}
